import { Box, Button, CircularProgress, Container, Paper, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_MOBILE_BACKEND_URL || "/api/v2";

const DeleteAccount = () => {
  const [step, setStep] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState("");
  const [clientName, setClientName] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/clients/login`, {
        email: email.trim().toLowerCase(),
        password,
      });

      const data = response.data;
      if (data?.status === "success" && data?.data?.token) {
        setToken(data.data.token);
        const client = data.data.client || {};
        setClientName(`${client.first_name || ""} ${client.last_name || ""}`.trim() || email);
        setStep("confirm");
      } else {
        setErrorMessage(data?.message || "Login failed. Please check your credentials.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.message ||
        "Login failed. Please check your credentials.";
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async () => {
    setErrorMessage("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE}/closures/closure-request`,
        { closure_request: true },
        { headers: { Authorization: token } }
      );

      const data = response.data;
      if (data?.status === "success") {
        setStep("success");
      } else {
        setErrorMessage(data?.message || "Failed to submit account deletion request.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.message ||
        "Failed to submit account deletion request. Please try again.";
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 3, sm: 5 },
            borderRadius: "16px",
            textAlign: "center",
          }}
        >
          {/* Logo */}
          <Box sx={{ marginBottom: 3 }}>
            <Box
              component="img"
              src="/assets/images/nativusLogo.png"
              alt="Nativus CoachMe"
              sx={{
                height: "41.41px",
                width: "200px",
                objectFit: "contain",
              }}
            />
          </Box>

          {step === "login" && (
            <form onSubmit={handleLogin}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#013D29", marginBottom: 1 }}>
                Delete Your Account
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", marginBottom: 3 }}>
                Log in with your Nativus CoachMe account to request account deletion.
              </Typography>

              {errorMessage && (
                <Typography
                  variant="body2"
                  sx={{ color: "#d32f2f", marginBottom: 2, backgroundColor: "#fdecea", padding: 1.5, borderRadius: 1 }}
                >
                  {errorMessage}
                </Typography>
              )}

              <Stack spacing={2.5}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !email || !password}
                  fullWidth
                  sx={{
                    backgroundColor: "#013D29",
                    height: 48,
                    textTransform: "none",
                    fontSize: "16px",
                    "&:hover": { backgroundColor: "#015a3e" },
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Log In"}
                </Button>
              </Stack>
            </form>
          )}

          {step === "confirm" && (
            <>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#013D29", marginBottom: 1 }}>
                Confirm Account Deletion
              </Typography>
              <Typography variant="body1" sx={{ marginBottom: 1 }}>
                Hi <strong>{clientName}</strong>,
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", marginBottom: 3, lineHeight: 1.7 }}>
                You are requesting to delete your Nativus account. By proceeding:
              </Typography>

              <Box
                sx={{
                  textAlign: "left",
                  backgroundColor: "#fff8e1",
                  padding: 2.5,
                  borderRadius: 2,
                  marginBottom: 3,
                  border: "1px solid #ffe082",
                }}
              >
                <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500 }}>
                  ⚠️ Please note:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ paddingLeft: 2, color: "#555", lineHeight: 2 }}>
                  <li>A notification will be sent to your coach to finalise your request.</li>
                  <li>Your coach will review and process the deletion.</li>
                  <li>Once approved, all your personal data will be permanently removed.</li>
                  <li>This action cannot be undone.</li>
                </Typography>
              </Box>

              {errorMessage && (
                <Typography
                  variant="body2"
                  sx={{ color: "#d32f2f", marginBottom: 2, backgroundColor: "#fdecea", padding: 1.5, borderRadius: 1 }}
                >
                  {errorMessage}
                </Typography>
              )}

              <Stack spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleDeleteRequest}
                  disabled={loading}
                  fullWidth
                  sx={{
                    backgroundColor: "#d32f2f",
                    height: 48,
                    textTransform: "none",
                    fontSize: "16px",
                    "&:hover": { backgroundColor: "#b71c1c" },
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Request Account Deletion"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setStep("login");
                    setToken("");
                    setErrorMessage("");
                  }}
                  fullWidth
                  sx={{
                    height: 48,
                    textTransform: "none",
                    fontSize: "16px",
                    borderColor: "#013D29",
                    color: "#013D29",
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </>
          )}

          {step === "success" && (
            <>
              <Box sx={{ fontSize: 48, marginBottom: 2 }}>✅</Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#013D29", marginBottom: 2 }}>
                Request Submitted
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.7, marginBottom: 3 }}>
                Your account deletion request has been submitted successfully. A notification has been sent to your
                coach. They will review and process your request. You will receive an email once your account has been
                deleted.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep("login");
                  setEmail("");
                  setPassword("");
                  setToken("");
                  setErrorMessage("");
                }}
                sx={{
                  textTransform: "none",
                  borderColor: "#013D29",
                  color: "#013D29",
                }}
              >
                Done
              </Button>
            </>
          )}

          {/* Footer */}
          <Typography variant="caption" sx={{ display: "block", marginTop: 4, color: "#999" }}>
            © {new Date().getFullYear()} Nativus. All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default DeleteAccount;
